// @ts-check

import { CartUpdateEvent, DiscountUpdateEvent } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * Bridges Shopify Storefront "standard events" onto the theme's internal
 * ThemeEvents bus.
 *
 * When an app, agent, or AI surface mutates the cart through the platform's
 * standard-actions bundle (`Shopify.actions.updateCart`), the bundle dispatches
 * `shopify:cart:*` standard events from the event target configured in
 * `standard-actions-override.js` (here: `document`). Our components are still
 * v3.5.1-shaped and listen for `cart:update` / `discount:update` on the
 * ThemeEvents bus, so this module relays each standard event into the matching
 * ThemeEvents event. Consumers then refresh themselves: `cart-icon` reads
 * `detail.data.itemCount`, and `cart-items-component` re-renders its section
 * via the Section Rendering API when no pre-rendered section HTML is provided.
 *
 * The theme's own components never dispatch `shopify:cart:*` events, so there
 * is no feedback loop: user-initiated updates flow ThemeEvents-only, and
 * external updates flow standard events -> (this bridge) -> ThemeEvents.
 *
 * @typedef {import('@shopify/events').CartLinesUpdateEvent} CartLinesUpdateEvent
 * @typedef {import('@shopify/events').CartNoteUpdateEvent} CartNoteUpdateEvent
 * @typedef {import('@shopify/events').CartDiscountUpdateEvent} CartDiscountUpdateEvent
 */

const SOURCE_ID = 'standard-events-bridge';

/**
 * Relays a standard cart lines/note mutation into a ThemeEvents CartUpdateEvent
 * once the mutation settles.
 *
 * `instanceof` checks are deliberately avoided: the standard-actions bundle
 * ships its own copies of the event classes, so identity checks against our
 * `@shopify/events` import would fail. Duck-type on `promise` instead.
 *
 * @param {Event} event - A `shopify:cart:lines-update` or `shopify:cart:note-update` event.
 */
async function handleCartMutation(event) {
  if (!('promise' in event)) return;

  const { promise } = /** @type {CartLinesUpdateEvent | CartNoteUpdateEvent} */ (event);

  try {
    const { cart } = await promise;
    if (!cart) return;

    document.dispatchEvent(
      new CartUpdateEvent(cart, SOURCE_ID, {
        source: SOURCE_ID,
        itemCount: cart.totalQuantity,
      })
    );
  } catch {
    // The mutation failed; the acting app/agent receives the error through the
    // action's own result. Nothing to sync in the theme UI.
  }
}

/**
 * Relays a standard cart discount mutation into a ThemeEvents
 * DiscountUpdateEvent once the mutation settles.
 *
 * @param {Event} event - A `shopify:cart:discount-update` event.
 */
async function handleDiscountMutation(event) {
  if (!('promise' in event)) return;

  const { promise } = /** @type {CartDiscountUpdateEvent} */ (event);

  try {
    const { cart } = await promise;
    if (!cart) return;

    document.dispatchEvent(new DiscountUpdateEvent(cart, SOURCE_ID));
  } catch {
    // See handleCartMutation.
  }
}

document.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => void handleCartMutation(event)
);
document.addEventListener(StandardEvents.cartNoteUpdate, (event) => void handleCartMutation(event));
document.addEventListener(
  StandardEvents.cartDiscountUpdate,
  (event) => void handleDiscountMutation(event)
);
